import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";
import type {
  CaseClass,
  ContentGetMiscContentFilteredRequest,
  CreateDiscoveryRequest,
  DocumentDetail,
  Interrogatory,
  MiscContent,
  Question,
} from "@/briefpoint-client";
import {
  DocumentSubType,
  DocumentType,
  MiscContentType,
  PartyPosition,
  PropoundingParty,
  Representative,
  Respondent,
} from "@/briefpoint-client";
import CaseInfo from "@/components/DocumentGeneration/DiscoveryRequests/CaseInfo";
import Definitions from "@/components/DocumentGeneration/DiscoveryRequests/Definitions";
import Questions from "@/components/DocumentGeneration/DiscoveryRequests/Questions";
import Loading from "@/components/Loading";
import ModalRequestAddFromLib from "@/components/Modals/DocumentWizard/PropoundDocumentReview/ModalRequestAddFromLib";
import {
  useCaseApi,
  useClientApi,
  useContentApi,
  useDefinitionsApi,
  useDocumentApi,
  useTagApi,
} from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useDocumentDetail from "@/hooks/useDocumentDetail";
import useDocuments from "@/hooks/useDocuments";
import useMiscContent from "@/hooks/useMiscContent";
import { punctuationRegexForDefAliases } from "@/utils";
import "./GenerateDiscoveryRequestPage.scss";
import raygun4js from "raygun4js";
import { ChevronDown, ChevronUp, InfoCircle } from "react-bootstrap-icons";
import type { ActionMeta, Options } from "react-select";
import CaseTypeSelect, {
  type CaseTypeOption,
} from "@/components/CaseManagement/CaseTypeSelect";
import type { QuestionListItems } from "@/components/DocumentWizard/PropoundDocumentReview";
import type { AddPosition } from "@/components/DocumentWizard/PropoundDocumentReview/types";
import {
  getRequestTypeSingular,
  replaceTokens,
} from "@/components/DocumentWizard/SpecialInterrogatoriesStep/Selections/utils";
import LeavingGuard from "@/components/LeavingGuard";
import TagsSelect, { type TagOption } from "@/components/Select/TagsSelect";
import { ADD_POSITION } from "@/lib/constants";
import type {
  CaseResponse,
  ClientResponse,
  DefinitionItem,
  DefinitionResponse,
  UserResponse,
} from "@/lib/types";
import { getUrl } from "@/lib/utils";
import {
  definitionRegex,
  excludeFromDefinitions,
} from "@/utils/Definitions/Definitions";
import { SaveQueue } from "@/utils/promiseQueue";

export function leadAttorneyName(
  _case: CaseResponse,
  client: ClientResponse,
  users: UserResponse[],
) {
  if (_case && client) {
    let primary =
      _case.attorneys?.find((x) => x.isPrimary) ??
      client.attorneys?.find((x) => x.isPrimary);

    if (!primary) {
      primary = _case.attorneys?.at(0) ?? client.attorneys?.at(0);
    }

    if (primary) {
      const attorney = users?.find((x) => x.id === primary?.id);

      if (attorney) {
        return `${attorney.lastName}, ${attorney.firstName}`;
      }
    }
  }

  return "-";
}

export interface ToastMsgItem {
  type: string;
  label: string;
}

export interface ToastMsgObj {
  item: ToastMsgItem[];
}

export interface QuestionItem extends Question {
  interrogatoryId?: number;
  isChecked: boolean;
  displayText?: string;
  saveToLibrary?: boolean;
  order?: number;
  tagIds?: number[] | null;
  isDeleted?: boolean;
}

export interface QuestionGroup {
  key: string;
  title: string;
  caseType?: string;
  tagIds?: number[];
  questions?: QuestionItem[];
  defaultQuestions?: MiscContent[];
}

const TOAST_TIMER = 5000;

function findWithin(text: string, searches?: string[]) {
  if (!text || !searches) return undefined;
  for (let i = 0; i < searches.length; i++) {
    const search = searches[i];

    if (text.includes(search)) {
      return search;
    }
  }

  return undefined;
}

function getDefinitions(
  text: string,
  existingDefinitions: DefinitionItem[],
  defs: DefinitionResponse[],
  exclude: string[],
) {
  const update = [...existingDefinitions];
  const missing: DefinitionItem[] = [];
  const definitionMatches = [...text.matchAll(definitionRegex)];
  const toExclude = excludeFromDefinitions.concat(exclude);

  if (!definitionMatches) return [];

  for (let m = 0; m < definitionMatches.length; m++) {
    let match = definitionMatches[m][0];

    //prevents matching on substitution strings
    if (match.startsWith("{") || toExclude.find((x) => x === match)) continue;

    for (let d = 0; d < defs.length; d++) {
      const definition = defs[d];
      if (!definition.term || definition.term.length < 3) {
        continue;
      }
      const termMatch = [definition.term];

      const parens = definition.term.indexOf("(");
      if (parens > -1) {
        try {
          // Just TERM of TERM(S)
          const singular = definition.term.substring(0, parens);
          termMatch.push(singular);
          // TERMS from TERM(S)
          const plural = definition.term.substring(
            parens + 1,
            definition.term.length - 1,
          );
          termMatch.push(singular.concat(plural));
        } catch (e) {
          console.error(e);
          raygun4js("send", {
            error: e,
            message: `Term was: ${definition.term}`,
          });
        }
      }
      let allSearches: string[] = [];
      try {
        allSearches = termMatch
          .concat(
            definition.aliases
              ?.split(punctuationRegexForDefAliases)
              ?.map((x) => x.trim()) ?? [],
          )
          .sort((a, b) => b.length - a.length);
      } catch (e) {
        console.error(e);
        console.error(`This alias failed: ${definition.aliases}`);
      }
      const searchFound = findWithin(match, allSearches);
      // TODO: do we need to do (S) filtering on aliases as above?
      if (searchFound) {
        const existing = update.findIndex((x) => x.term === definition.term);
        // TODO: I can't figure out a clever way to not have duplicate code here
        if (existing === -1) {
          update.push(definition);
          //Need to potentially add definitions for terms inside other definitions
          const toAdd =
            getDefinitions(definition.content ?? "", update, defs, exclude) ??
            [];
          toAdd
            .filter((a) => !update.find((u) => u.term === a.term))
            .forEach((a) => {
              update.push(a);
            });
        } else if (!update[existing].id) {
          update[existing] = definition;
          //Need to potentially add definitions for terms inside other definitions
          const toAdd =
            getDefinitions(definition.content ?? "", update, defs, exclude) ??
            [];
          toAdd
            .filter((a) => !update.find((u) => u.term === a.term))
            .forEach((a) => {
              update.push(a);
            });
        }
        match = match.replace(searchFound, "").trim();

        if (match?.length < 3) {
          break;
        }
      }
    }

    if (match?.length >= 3) {
      missing.push({ term: match } as DefinitionItem);
    }
  }

  return update.concat(missing);
}

export function buildQuestionKey(question: MiscContent) {
  let key = "-";

  if (question.caseTypeId) {
    key += question.caseTypeId;
  }

  if (question.tagIds?.length) {
    key += `tags${question.tagIds.sort().join("-")}`;
  }

  return key;
}

export function buildQuestionTitle(
  question: MiscContent,
  type: DocumentSubType,
  caseClasses: CaseClass[],
  allTags: Map<number, string>,
) {
  if (!question.caseTypeId && !question.tagIds?.length) {
    return `General ${getRequestTypeSingular(type, "")}`;
  }

  let caseTypeLabel = "";
  if (question.caseTypeId) {
    const caseClass = caseClasses.find((x) => x.id === question.caseTypeId);

    if (caseClass) {
      caseTypeLabel = caseClass.name ?? "";
    } else {
      const caseType = caseClasses
        .flatMap((x) => x.subTypes)
        .find((x) => x?.id === question.caseTypeId);
      caseTypeLabel = caseType?.fullName ?? "";
    }
  }

  let tagsLabel = "";
  if (question.tagIds?.length) {
    const tags: string[] = [];
    question.tagIds.sort().forEach((t) => {
      tags.push(allTags.get(t) ?? "");
    });

    for (let i = 0; i < tags.length; i++) {
      const tag = tags[i];

      if (!tagsLabel) {
        tagsLabel = tag;
      } else {
        tagsLabel += `, ${tag}`;
      }
    }
  } else {
    return caseTypeLabel;
  }

  if (!caseTypeLabel) {
    return tagsLabel;
  }

  return `${caseTypeLabel}: ${tagsLabel}`;
}

export function convertPartyPositionToPropoundingParty(
  position?: PartyPosition,
) {
  let ret = PropoundingParty.Unknown;

  if (!position) return ret;
  // need to do this jazz because UserService enums are being returned as strings not numbers like the DocumentService,
  // probably need to change UserService to behave like DocumentService but there are too many cases to handle right now
  const positionValues = position.split(",").map((item) => item.trim());
  for (let i = 0; i < positionValues.length; i++) {
    const p = positionValues[i];

    if (p === PartyPosition.Plaintiff) {
      ret += PropoundingParty.Plaintiff;
    } else if (p === PartyPosition.Defendant) {
      ret += PropoundingParty.Defendant;
    } else if (p === PartyPosition.CrossComplainant) {
      ret += PropoundingParty.CrossComplainant;
    } else if (p === PartyPosition.CrossDefendant) {
      ret += PropoundingParty.CrossDefendant;
    }
  }

  return ret;
}

export default function GenerateDiscoveryRequestPage() {
  const { user, firm } = useAuth();
  const location = useLocation<{
    client: ClientResponse;
    case: CaseResponse;
  }>();
  const { clientId, id } = useParams<{ clientId: string; id: string }>();
  const clientApi = useClientApi();
  const caseApi = useCaseApi();
  const [, , , ,] = useDocuments(false);
  const [client, setClient] = useState<ClientResponse | undefined>(
    location.state?.client,
  );
  const [_case, setCase] = useState<CaseResponse | undefined>(
    location.state?.case,
  );
  const [caseIsDirty, setCaseIsDirty] = useState(false);
  const [saving, setSaving] = useState<string>();
  const [, setErroredSave] = useState<() => Promise<void>>();
  const typeParam = new URLSearchParams(location.search).get("type");
  const setParam = new URLSearchParams(location.search).get("set");
  const startingParam = new URLSearchParams(location.search).get("start");
  const partyId = new URLSearchParams(location.search).get("partyId");
  const documentId = new URLSearchParams(location.search).get("documentId");
  const [questions, setQuestions] = useState<QuestionGroup[]>();
  const [checkedItems, setCheckedItems] = useState<QuestionListItems[]>([]);
  const [availableDefinitions, setAvailableDefinitions] =
    useState<DefinitionResponse[]>();
  const [definitions, setDefinitions] = useState<DefinitionItem[]>([]);
  const [definitionsExtracted, setDefinitionsExtracted] = useState(false);
  const [availableTags, setAvailableTags] = useState<
    Map<number, string> | undefined
  >();
  const [extraTagInfo, setExtraTagInfo] = useState<
    Map<number, string> | undefined
  >();
  const [setNumber, setSetNumber] = useState(setParam ? Number(setParam) : 1);
  const [startingNumber, setStartingNumber] = useState<number>(
    startingParam ? Number(startingParam) : 1,
  );
  const [document, setDocument] = useState<DocumentDetail>();
  const [defaultQuestions, setDefaultQuestions] = useState<MiscContent[]>();
  const [, , , , , deleteInterrogatory] = useDocumentDetail(document?.id ?? "");
  const [dirty, setIsDirty] = useState(false);
  const { jurisdictions } = useAuth();
  const jx = (jurisdictions ?? []).find((x) => x.id === _case?.jurisdiction);
  const caseTypes = jx?.caseClasses
    ?.filter((c) => c.subTypes)
    .flatMap((c) => [c.subTypes ?? []])
    .flat();
  const docType =
    document?.info?.documentSubType ??
    (typeParam ? (Number(typeParam) as DocumentSubType) : undefined);
  const [addPosition, setAddPosition] = useState<AddPosition>(
    ADD_POSITION.START,
  );
  const [caseDetailsOpen, setCaseDetailsOpen] = useState<boolean>(true);
  const definitionsApi = useDefinitionsApi();
  const [, , , , miscQuestions, , createMiscContent] = useMiscContent();
  const documentApi = useDocumentApi();
  const contentApi = useContentApi();
  const tagApi = useTagApi();
  const scrollToQuestion = useRef<HTMLDivElement | null>(null);
  const documentMatchedToQuestions = useRef(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastRequestOpen, setToastRequestOpen] = useState<boolean>(false);
  const [toastPromptType, setToastPromptType] = useState<string>();
  const [toastMsgValue, setToastMsgValue] = useState<string>();
  const [toastCurTagId, setToastCurTagId] = useState<number | undefined | null>(
    null,
  );
  const [toastExistingTag, setToastExistingTag] = useState<boolean | null>(
    null,
  );
  const party = _case
    ? _case.otherParties?.find(
        (x) => x.id === (document?.info?.otherPartyId ?? partyId),
      )
    : undefined;
  const updateQueue = useRef(new SaveQueue(setSaving, setErroredSave));
  const isFirstTagRender = useRef(true);
  const prevTagIds = useRef<number[]>([]);
  // For Library Modal
  const [showLibrary, setShowLibrary] = useState<boolean>(false);
  const [doTagsExistInLibrary, setDoTagsExistInLibrary] =
    useState<boolean>(false);
  const [modalSaving, setModalSaving] = useState<boolean>(false);
  const [tagSectionToOpen, setTagSectionToOpen] = useState<string>("");
  const [tagIdToFind, setTagIdToFind] = useState<number | undefined>(undefined);
  const filteredSourceIdRequests = questions
    ?.flatMap((group) => group.questions ?? [])
    .filter((item) => item.questionId && item.questionId.trim() !== "")
    .map(
      (item): Interrogatory => ({
        id: item.interrogatoryId ?? 0,
        documentId: document?.id ?? "",
        number: item.number ?? 0,
        text: item.text ?? "",
        complete: false,
        sourceId: item.questionId,
        response: {},
        responseList: {},
      }),
    );

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch typeParam
  useEffect(() => {
    setQuestions(undefined);
    setDefinitions([]);
    setDocument(undefined);
    setDefaultQuestions(undefined);
  }, [typeParam]);

  const loadClient = useCallback(
    async (
      user: UserResponse | undefined,
      client: ClientResponse | undefined,
    ) => {
      if (user?.firmId && !client) {
        const client = (await clientApi.clientGet({
          firmId: user.firmId,
          id: clientId,
        })) as ClientResponse;
        setClient(client);
      }
    },
    [clientApi, clientId],
  );
  const requestType = docType && getRequestTypeSingular(docType, jx?.id ?? "");
  useEffect(() => {
    loadClient(user, client);
  }, [user, loadClient, client]);

  const loadCase = useCallback(
    async (user: UserResponse | undefined, _case: CaseResponse | undefined) => {
      if (user?.firmId && !_case) {
        const c = (await caseApi.caseGet({
          firmId: user.firmId,
          id,
        })) as CaseResponse;
        setCase(c);
      }
    },
    [caseApi, id],
  );

  useEffect(() => {
    loadCase(user, _case);
  }, [user, loadCase, _case]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch setAvailableDefinitions
  useEffect(() => {
    async function loadDefs() {
      const defs = (await definitionsApi.definitionGetList({
        caseId: id,
      })) as DefinitionResponse[];
      defs.sort((a, b) => (b.term?.length ?? 0) - (a.term?.length ?? 0));
      setAvailableDefinitions(defs);
    }

    if (!availableDefinitions?.length && id) {
      loadDefs();
    }
  }, [availableDefinitions, setAvailableDefinitions, definitionsApi, id]);

  // Store plaintiff/defendants value in individual variables to pass in the useCallback. Prevents the useCallBack from rerunning if anything WITHIN the caseInfo object changes.
  // We only need it to track changes on the 'plaintiff' / 'defendants' values only.
  const caseInfoPtf = _case?.plaintiffs;
  const caseInfoDef = _case?.defendants;
  const docName = document?.name ?? "";

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch setDefinitions
  const extractDefinitionsFromQuestions = useCallback(
    async (questions: QuestionItem[]) => {
      if (!availableDefinitions || !client || !party) return;

      // Only process checked questions for definition extraction
      const checkedQuestions = questions.filter((q) => q.isChecked);

      const excludeTerms = [
        docName,
        party?.name ?? "",
        caseInfoPtf ?? "",
        `PLAINTIFF ${caseInfoPtf ?? ""}`,
        caseInfoDef ?? "",
        `DEFENDANT ${caseInfoDef ?? ""}`,
      ];

      // Collect all terms that should be defined based on checked questions
      const neededDefinitionTerms = new Set<string>();
      const neededDefinitions: DefinitionItem[] = [];

      for (let i = 0; i < checkedQuestions.length; i++) {
        const text =
          checkedQuestions[i].displayText ?? checkedQuestions[i].text;

        if (!text) continue;

        const foundDefinitions = getDefinitions(
          text,
          [],
          availableDefinitions,
          excludeTerms,
        );

        for (let a = 0; a < foundDefinitions.length; a++) {
          const element = foundDefinitions[a];

          if (element.term && !neededDefinitionTerms.has(element.term)) {
            neededDefinitionTerms.add(element.term);
            neededDefinitions.push(element);
          }
        }
      }

      // Get all terms that could potentially be extracted from ANY question (checked or unchecked)
      // This helps us identify which definitions were extracted from questions vs manually added
      const allPossibleExtractedTerms = new Set<string>();

      for (let i = 0; i < questions.length; i++) {
        const text = questions[i].displayText ?? questions[i].text;
        if (!text) continue;

        const foundDefinitions = getDefinitions(
          text,
          [],
          availableDefinitions,
          excludeTerms,
        );
        foundDefinitions.forEach((def) => {
          if (def.term) allPossibleExtractedTerms.add(def.term);
        });
      }

      // Update definitions state
      setDefinitions((currentDefinitions) => {
        const current = currentDefinitions ?? [];

        // Keep existing definitions that are:
        // 1. Still needed (from checked questions), OR
        // 2. Manually created (not extracted from any question text)
        const definitionsToKeep = current.filter((def) => {
          const isStillNeeded = neededDefinitionTerms.has(def.term || "");
          const wasExtractedFromQuestion = allPossibleExtractedTerms.has(
            def.term || "",
          );
          const isManuallyCreated = !wasExtractedFromQuestion;

          return isStillNeeded || isManuallyCreated;
        });

        // Add new definitions that aren't already present
        const newDefinitions = neededDefinitions.filter(
          (newDef) =>
            !definitionsToKeep.find(
              (existing) => existing.term === newDef.term,
            ),
        );

        const finalDefinitions = [...definitionsToKeep, ...newDefinitions];

        return finalDefinitions.sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      });
    },
    [
      setDefinitions,
      availableDefinitions,
      client,
      party,
      caseInfoPtf,
      caseInfoDef,
      docName,
    ],
  );

  useEffect(() => {
    if (
      questions &&
      availableDefinitions &&
      (documentMatchedToQuestions.current || !definitionsExtracted)
    ) {
      const loadDefs = async () => {
        await extractDefinitionsFromQuestions(
          questions.flatMap((x) => x.questions ?? []),
        );
        setDefinitionsExtracted(true);
      };
      loadDefs();
    }
  }, [
    questions,
    availableDefinitions,
    extractDefinitionsFromQuestions,
    definitionsExtracted,
  ]);

  useEffect(() => {
    async function loadDoc() {
      const doc = await documentApi.documentGet({
        documentId: documentId ?? "",
      });
      setDocument(doc);
      setSetNumber((s) => doc.info?.setNumber ?? s);
      setStartingNumber((s) => doc.info?.startingNumber ?? s);
    }

    if (documentId && (!document || document.id !== documentId)) {
      loadDoc();
    }
  }, [documentId, document, documentApi]);

  function sortQuestionGroup(a: QuestionGroup, b: QuestionGroup) {
    if (a.key === "-1") {
      return 1;
    }

    if (b.key === "-1") {
      return -1;
    }

    return a.key.localeCompare(b.key);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore sortQuestionGroup
  useEffect(() => {
    if (
      questions &&
      document &&
      _case?.representativeType &&
      !documentMatchedToQuestions.current
    ) {
      const update = [...(questions ?? [])];

      update.forEach((g) => {
        g.questions
          ?.filter((q) => q.questionId)
          .forEach((q) => {
            const matching = document.interrogatories.find(
              (x) => x.sourceId === q.questionId,
            );
            q.isChecked = !!matching;
            if (matching) {
              q.text = matching.text;
              q.displayText = replaceTokens(
                document.info,
                matching.text || "",
                true,
                _case?.representativeType,
              );
              q.interrogatoryId = matching.id;
              q.number = matching.number;
            }
          });
      });

      let group = update.find((x) => x.key === "-1");

      for (let i = 0; i < document.interrogatories.length; i++) {
        const int = document.interrogatories[i];

        // Check if an interrogatory has a sourceId. If it does, it means it was added/created by the user in the library.
        if (!int.sourceId) {
          if (!group) {
            group = { key: "-1", title: "Document Specific", questions: [] };
            update.push(group);
          }
          if (
            !group.questions?.find(
              (x) => x.interrogatoryId === int.id || x.text === int.text,
            )
          ) {
            group.questions?.push({
              isChecked: true,
              text: int.text,
              displayText: replaceTokens(
                document.info,
                int.text || "",
                true,
                _case?.representativeType,
              ),
              interrogatoryId: int.id,
              questionId: int.sourceId,
              number: int.number,
            });
          }
          const unsaved = group.questions?.find(
            (x) => !x.interrogatoryId && x.text === int.text,
          );
          if (unsaved) {
            unsaved.interrogatoryId = int.id;
          }
        } else {
          // Check to see if the interrogatory is already in the questions array.
          const questionExists = update.some((g) =>
            g.questions?.some((q) => q.questionId === int.sourceId),
          );

          if (!questionExists) {
            let miscContent = miscQuestions?.find((q) => q.id === int.sourceId);
            if (!miscContent) {
              miscContent = defaultQuestions?.find(
                (q) => q.id === int.sourceId,
              );
            }

            if (miscContent) {
              const key = buildQuestionKey(miscContent);
              let targetGroup = update.find((x) => x.key === key);

              if (!targetGroup) {
                targetGroup = {
                  key,
                  title: buildQuestionTitle(
                    miscContent,
                    docType ?? DocumentSubType.Unknown,
                    jx?.caseClasses ?? [],
                    availableTags ?? new Map(),
                  ),
                  caseType: miscContent.caseTypeId ?? undefined,
                  tagIds: miscContent.tagIds ?? [],
                  questions: [],
                };
                update.push(targetGroup);
              }

              if (
                !targetGroup.questions?.find(
                  (x) => x.interrogatoryId === int.id,
                )
              ) {
                targetGroup.questions?.push({
                  isChecked: true,
                  text: int.text,
                  displayText: replaceTokens(
                    document.info,
                    int.text || "",
                    true,
                    _case?.representativeType,
                  ),
                  interrogatoryId: int.id,
                  questionId: int.sourceId,
                  number: int.number,
                });
              }
            } else {
              if (!group) {
                group = {
                  key: "-1",
                  title: "Document Specific",
                  questions: [],
                };
                update.push(group);
              }

              if (!group.questions?.find((x) => x.interrogatoryId === int.id)) {
                group.questions?.push({
                  isChecked: true,
                  text: int.text,
                  displayText: replaceTokens(
                    document.info,
                    int.text || "",
                    true,
                    _case?.representativeType,
                  ),
                  interrogatoryId: int.id,
                  questionId: int.sourceId,
                  number: int.number,
                });
              }
            }
          }
        }
      }

      setQuestions(update.sort(sortQuestionGroup));
      return () => {
        documentMatchedToQuestions.current = true;
      };
    }
  }, [
    questions,
    document,
    _case?.representativeType,
    miscQuestions,
    defaultQuestions,
    docType,
    jx,
    availableTags,
  ]);

  useEffect(() => {
    if (_case?.caseType && docType && party) {
      const query: ContentGetMiscContentFilteredRequest = {
        type: MiscContentType.DiscoveryQuestion,
        documentType: DocumentType.DiscoveryRequest,
        caseType: _case.caseType ?? undefined,
        documentSubType: docType,
        skipTagFiltering: true,
        isForRepresentative:
          _case.representativeType === undefined ||
          _case.representativeType === Representative.None
            ? false
            : _case.representativeType !== Representative.Individual,
        respondingParty:
          party?.position === undefined ||
          party.position === PartyPosition.Unknown
            ? undefined
            : convertPartyPositionToPropoundingParty(party.position),
      };
      const extras = new Map<number, number>();
      Promise.all([
        contentApi.contentGetMiscContentFiltered(query),
        contentApi.contentGetDefaultMiscContentFiltered(query),
      ]).then(([update, defaultQuestions]) => {
        update
          .flatMap((x) => x.tagIds ?? [])
          .forEach((x) => {
            const found = extras.get(x);
            extras.set(x, found ? found + 1 : 1);
          });

        defaultQuestions
          .flatMap((x) => x.tagIds ?? [])
          .forEach((x) => {
            const found = extras.get(x);
            extras.set(x, found ? found + 1 : 1);
          });

        // Convert numbers to strings for TagsSelect component
        const extrasAsStrings = new Map<number, string>();
        extras.forEach((value, key) => {
          extrasAsStrings.set(key, value.toString());
        });
        setExtraTagInfo(extrasAsStrings);
      });
    }
  }, [_case, contentApi, docType, party]);

  const handleCheckboxChange = (
    id: string | undefined,
    checked: boolean,
    parentTag: string,
    content: string | null | undefined,
    question?: MiscContent,
  ) => {
    if (checked) {
      setCheckedItems((prevItems) => {
        if (!prevItems.some((item) => item.id === id)) {
          const newItems = [
            ...prevItems,
            { id, content, checked, parentTag, question },
          ];
          return newItems.sort((a, b) =>
            a.parentTag.localeCompare(b.parentTag),
          );
        }
        return prevItems;
      });
    } else {
      setCheckedItems(checkedItems.filter((item) => item.id !== id));
    }
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPosition = e.target.value as AddPosition;
    setAddPosition(newPosition);
  };

  const handleShowLibrary = () => {
    setShowLibrary(false);
    if (checkedItems) setCheckedItems([]);
    if (tagSectionToOpen) setTagSectionToOpen("");
    if (tagIdToFind) setTagIdToFind(undefined);
    resetToastParams();
  };

  const handleOpenLibrary = (tagSectionToOpen?: string, tagId?: number) => {
    setShowLibrary(true);
    if (tagSectionToOpen) setTagSectionToOpen(tagSectionToOpen);
    if (tagId) setTagIdToFind(tagId);
  };

  const handleDeleteItem = async (id: number | undefined) => {
    if (!id) return;
    setSaving("saving");
    try {
      await deleteInterrogatory(id, document?.id);
      setQuestions((prevState) => {
        if (!prevState) return prevState;
        return prevState
          .map((group) => ({
            ...group,
            questions: group.questions?.filter((q) => q.interrogatoryId !== id),
          }))
          .filter((group) => group.questions && group.questions.length > 0);
      });
    } catch (error) {
      console.error(error);
    }
    setSaving(undefined);
  };

  const addToGeneratedList = async () => {
    setModalSaving(true);
    const itemsToAdd = checkedItems
      .filter((item) => item.checked && item.content && item.id)
      .map((item) => ({
        id: item.id ?? "",
        text: item.content ?? "",
        sourceId: item.id ?? "",
        question: item.question,
      }));

    try {
      let updatedQuestions: QuestionGroup[] = [];
      const updated = [...(questions ?? [])];
      const groupedItems = new Map<
        string,
        { items: typeof itemsToAdd; question: MiscContent }
      >();

      itemsToAdd.forEach((item) => {
        if (item.question) {
          const key = buildQuestionKey(item.question);

          if (!groupedItems.has(key)) {
            groupedItems.set(key, { items: [], question: item.question });
          }
          groupedItems.get(key)?.items.push(item);
        } else {
          const key = "-";
          if (!groupedItems.has(key)) {
            groupedItems.set(key, {
              items: [],
              question: {
                caseTypeId: undefined,
                tagIds: [],
                content: item.text,
              } as MiscContent,
            });
          }
          groupedItems.get(key)?.items.push(item);
        }
      });

      groupedItems.forEach((groupData, key) => {
        let existingGroup = updated.find((g) => g.key === key);

        if (!existingGroup) {
          existingGroup = {
            key,
            title:
              key === "-"
                ? `General ${getRequestTypeSingular(docType ?? DocumentSubType.Unknown, "")}`
                : buildQuestionTitle(
                    groupData.question,
                    docType ?? DocumentSubType.Unknown,
                    jx?.caseClasses ?? [],
                    availableTags ?? new Map(),
                  ),
            caseType: groupData.question.caseTypeId ?? undefined,
            tagIds: groupData.question.tagIds ?? [],
            questions: [],
          };
          updated.push(existingGroup);
        }

        const newQuestions = groupData.items.map((item) => ({
          text: item.text,
          sourceId: item.id,
          isChecked: true,
          questionId: item.id,
          displayText: replaceTokens(
            {
              propoundingParty: convertPartyPositionToPropoundingParty(
                _case?.clientPosition,
              ),
              documentType: DocumentType.DiscoveryRequest,
              documentSubType: docType ?? DocumentSubType.Unknown,
              respondent: Respondent.Custom,
              respondentCustom: party?.name,
              respondingPartyName: party?.name,
              propoundingPartyName: client?.name,
              respondingParty: convertPartyPositionToPropoundingParty(
                party?.position,
              ),
            },
            item.text || "",
            true,
            _case?.representativeType,
          ),
        }));

        if (addPosition === ADD_POSITION.START) {
          existingGroup.questions = [
            ...newQuestions,
            ...(existingGroup.questions ?? []),
          ];
        } else {
          existingGroup.questions = [
            ...(existingGroup.questions ?? []),
            ...newQuestions,
          ];
        }
      });

      updatedQuestions = updated.sort(sortQuestionGroup);

      await new Promise((resolve) => setTimeout(resolve, 0));

      const allQuestions = updatedQuestions.flatMap(
        (group) => group.questions ?? [],
      );

      const createDiscoveryRequest: CreateDiscoveryRequest = {
        caseId: _case?.id ?? "",
        clientId: client?.id ?? "",
        setNumber: setNumber,
        startingNumber: startingNumber,
        documentSubType: docType,
        respondingId: party?.id,
        questions: allQuestions.map<Question>((q, index) => {
          return {
            questionId: q?.questionId,
            text: q?.text,
            number: index + 1,
            interrogatoryId: q?.interrogatoryId,
          };
        }),
      };

      let docId = document?.id;
      if (!docId) {
        const created = await documentApi.documentCreateDiscoveryRequest({
          createDiscoveryRequest,
        });
        updateDocumentAndReloadQuestions(created);
        docId = created.id;
      } else {
        const updated = await documentApi.documentPutDiscoveryRequest({
          documentId: docId,
          createDiscoveryRequest,
        });

        updateDocumentAndReloadQuestions(updated);
      }

      setCheckedItems([]);
      resetToastParams();
    } catch (e) {
      console.error(e);
    }

    setShowLibrary(false);
    setModalSaving(false);
  };

  const determineCheck = (id: string | undefined) => {
    if (id) {
      const findMatchingContent = checkedItems.find((item) => item.id === id);
      if (findMatchingContent) {
        return findMatchingContent.checked && true;
      }
      return false;
    }
    return false;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore sortQuestionGroup
  const loadQuestions = useCallback(() => {
    const query: ContentGetMiscContentFilteredRequest = {
      type: MiscContentType.DiscoveryQuestion,
      documentType: DocumentType.DiscoveryRequest,
      documentSubType: docType,
      caseType: _case?.caseType ?? undefined,
      tagIds:
        !!_case?.tagIds?.length || !!party?.tagIds?.length
          ? (_case?.tagIds ?? []).concat(party?.tagIds ?? [])
          : undefined,
      isForRepresentative:
        _case?.representativeType === undefined ||
        _case.representativeType === Representative.None
          ? false
          : _case.representativeType !== Representative.Individual,
      respondingParty:
        party?.position === undefined ||
        party.position === PartyPosition.Unknown
          ? undefined
          : convertPartyPositionToPropoundingParty(party.position),
    };
    const defaultQuery: ContentGetMiscContentFilteredRequest = {
      type: MiscContentType.DiscoveryQuestion,
      documentType: DocumentType.DiscoveryRequest,
      documentSubType: docType,
      caseType: _case?.caseType ?? undefined,
      skipTagFiltering: true,
    };
    Promise.all([
      contentApi.contentGetMiscContentFiltered(query),
      contentApi.contentGetDefaultMiscContentFiltered(defaultQuery),
    ]).then(([update, defaultQuestionsData]) => {
      // Store defaultQuestions in state so the useEffect can access it
      setDefaultQuestions(defaultQuestionsData);

      const questionCountForTags = new Map<number, number>();

      update
        .flatMap((x) => x.tagIds ?? [])
        .forEach((x) => {
          const found = questionCountForTags.get(x);
          questionCountForTags.set(x, found ? found + 1 : 1);
        });

      defaultQuestionsData
        .flatMap((x) => x.tagIds ?? [])
        .forEach((x) => {
          const found = questionCountForTags.get(x);
          questionCountForTags.set(x, found ? found + 1 : 1);
        });

      if (toastCurTagId) {
        if (!questionCountForTags.has(toastCurTagId as number)) {
          // setToastExistingTag(false);
        } else {
          // setToastExistingTag(true);
        }
      }

      // Make a copy of existing questions to preserve the 'isChecked' state of questions when making updates to them
      const existingQuestionStates = new Map();

      if (questions) {
        questions.forEach((group) => {
          group.questions?.forEach((q) => {
            if (q.questionId) {
              existingQuestionStates.set(q.questionId, {
                isChecked: q.isChecked,
                interrogatoryId: q.interrogatoryId,
                number: q.number,
              });
            }
          });
        });
      }

      setQuestions((existing) => {
        const groups = [...(existing ?? [])];
        defaultQuestionsData.forEach((question) => {
          const key = buildQuestionKey(question);

          let group = groups.find((x) => x.key === key);
          if (!group) {
            group = {
              key,
              title: buildQuestionTitle(
                question,
                docType ?? DocumentSubType.Unknown,
                jx?.caseClasses ?? [],
                availableTags ?? new Map(),
              ),
              caseType: question.caseTypeId ?? undefined,
              tagIds: question.tagIds ?? [],
              questions: [],
              defaultQuestions: [question],
            };
            groups.push(group);
          } else if (
            !group.defaultQuestions?.find((x) => x.id === question.id)
          ) {
            group.defaultQuestions?.push(question);
          }
        });

        if (setNumber <= 1) {
          update.forEach((question) => {
            const key = buildQuestionKey(question);

            let group = groups.find((x) => x.key === key);

            // Check if there's an existing interrogatory in document.interrogatories with this sourceId
            const existingInterrogatory = document?.interrogatories?.find(
              (int) => int.sourceId === question.id,
            );

            // Check if the question already exist, if it does, get its previous state
            const existingState = existingQuestionStates.get(question.id);

            let q: QuestionItem;

            if (existingInterrogatory) {
              // Use data from existing interrogatory to preserve the document state
              q = {
                ...question,
                questionId: question.id,
                order: question.order ?? undefined,
                isChecked: true, // Mark as checked since it exists in the document
                text: existingInterrogatory.text, // Use the text from the document
                interrogatoryId: existingInterrogatory.id,
                number: existingInterrogatory.number,
                displayText: replaceTokens(
                  {
                    propoundingParty: convertPartyPositionToPropoundingParty(
                      _case?.clientPosition,
                    ),
                    documentType: DocumentType.DiscoveryRequest,
                    documentSubType: docType ?? DocumentSubType.Unknown,
                    respondent: Respondent.Custom,
                    respondentCustom: party?.name,
                    respondingPartyName: party?.name,
                    propoundingPartyName: client?.name,
                    respondingParty: convertPartyPositionToPropoundingParty(
                      party?.position,
                    ),
                  },
                  existingInterrogatory.text || "", // Use the text from the document
                  true,
                  _case?.representativeType,
                ),
              };
            } else {
              // Create new question item as before
              q = {
                ...question,
                questionId: question.id,
                order: question.order ?? undefined,
                // If a new question is added? Default to true, otherwise, reference the existing question's current state.
                isChecked: existingState ? existingState.isChecked : true,
                text: question.content,
                displayText: replaceTokens(
                  {
                    propoundingParty: convertPartyPositionToPropoundingParty(
                      _case?.clientPosition,
                    ),
                    documentType: DocumentType.DiscoveryRequest,
                    documentSubType: docType ?? DocumentSubType.Unknown,
                    respondent: Respondent.Custom,
                    respondentCustom: party?.name,
                    respondingPartyName: party?.name,
                    propoundingPartyName: client?.name,
                    respondingParty: convertPartyPositionToPropoundingParty(
                      party?.position,
                    ),
                  },
                  question.content || "",
                  true,
                  _case?.representativeType,
                ),
              };
            }

            const matchTagId = q.tagIds?.includes(toastCurTagId as number);

            if (matchTagId) {
              if (q.text !== "") {
                setToastExistingTag(true);
              } else {
                setToastExistingTag(false);
              }
            }

            if (!group) {
              group = {
                key,
                title: buildQuestionTitle(
                  question,
                  docType ?? DocumentSubType.Unknown,
                  jx?.caseClasses ?? [],
                  availableTags ?? new Map(),
                ),
                caseType: question.caseTypeId ?? undefined,
                tagIds: question.tagIds ?? [],
                questions: [q],
              };
              groups.push(group);
            } else if (
              !group.questions?.find((x) => x.questionId === question.id)
            ) {
              group.questions?.push(q);
            } else {
              // Update existing question, but preserve their checked state and interrogatory data if it exists
              const existingQuestionIndex = group.questions.findIndex(
                (x) => x.questionId === question.id,
              );
              if (existingQuestionIndex !== -1) {
                const existingQuestion = group.questions[existingQuestionIndex];
                group.questions[existingQuestionIndex] = {
                  ...q,
                  isChecked: existingQuestion.isChecked,
                  // Preserve interrogatory data if it already exists
                  interrogatoryId:
                    existingQuestion.interrogatoryId || q.interrogatoryId,
                  number: existingQuestion.number || q.number,
                };
              }
            }
          });
        }
        const mapped = groups.sort(sortQuestionGroup);
        return mapped;
      });

      if (toastMsgValue !== "" && caseIsDirty) {
        setShowToast(true);
      }
    });
  }, [
    _case,
    caseIsDirty,
    contentApi,
    docType,
    availableTags,
    jx,
    client,
    party,
    setNumber,
    questions,
    toastCurTagId,
    toastMsgValue,
    document, // Add document to dependencies
  ]);

  // Reusable function to update document state and reload questions
  const updateDocumentAndReloadQuestions = useCallback(
    (updatedDocument: DocumentDetail) => {
      setDocument(updatedDocument);
      // Reset the document matched flag to force reloading questions with new data
      documentMatchedToQuestions.current = false;
      // Trigger loadQuestions to rerun with the updated interrogatories
      loadQuestions();
    },
    [loadQuestions],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore contentApi
  useEffect(() => {
    if (
      _case &&
      client &&
      availableTags &&
      jx?.caseClasses &&
      docType &&
      party
    ) {
      if (!questions) {
        loadQuestions();
        documentMatchedToQuestions.current = false;
      }
    }
  }, [
    _case,
    contentApi,
    docType,
    availableTags,
    jx,
    client,
    party,
    questions,
    loadQuestions,
  ]);

  useEffect(() => {
    // Store tags initially on the first render, the below will only fire off ONLY when tags have been updated for this draft.
    if (isFirstTagRender.current) {
      isFirstTagRender.current = false;
      if (_case?.tagIds) {
        prevTagIds.current = [..._case.tagIds];
      }
      return;
    }

    if (
      _case?.tagIds &&
      client &&
      availableTags &&
      jx?.caseClasses &&
      docType &&
      party
    ) {
      const currentTagIds = _case?.tagIds || [];
      const previousTagIds = prevTagIds.current;

      const tagIdsChanged =
        previousTagIds.length !== currentTagIds.length ||
        previousTagIds.some((id) => !currentTagIds.includes(id)) ||
        currentTagIds.some((id) => !previousTagIds.includes(id));

      if (tagIdsChanged) {
        loadQuestions();
        prevTagIds.current = [...currentTagIds];
      }
    }
  }, [_case?.tagIds, client, availableTags, jx, docType, party, loadQuestions]);

  useEffect(() => {
    async function loadTags() {
      const tagsList = new Map<number, string>();

      const loadedTags = await tagApi.tagFindTags({});

      loadedTags.forEach((t) => {
        tagsList.set(t.id, t.name);
      });
      setAvailableTags(tagsList);
    }
    loadTags();
  }, [tagApi]);

  const caseTags =
    availableTags &&
    new Map<number, string>(
      Array.from(availableTags).filter(
        ([index]) => !party || !party.tagIds?.includes(index),
      ),
    );

  const resetToastParams = (): void => {
    setShowToast(false);
    setToastExistingTag(null);
    setToastMsgValue("");
    setDoTagsExistInLibrary(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore resetToastParams
  useEffect(() => {
    if (showToast) {
      function handleToastVisibility() {
        const timeoutMsg = setTimeout(() => {
          resetToastParams();
        }, TOAST_TIMER);

        return () => clearTimeout(timeoutMsg);
      }

      handleToastVisibility();
    }
  }, [showToast]);

  function handleCaseChange(value: string, field: keyof CaseResponse) {
    setCaseIsDirty(true);
    setCase((c) => {
      if (!c) return c;
      return { ...c, [field]: value };
    });
  }

  function handleDefinitionChange(
    value: unknown,
    index: number,
    field: keyof DefinitionItem,
  ) {
    setDefinitions((ds) => {
      if (!ds) return ds;
      const update = [...ds];
      const item = update[index];
      if (!item) return update;
      (item as DefinitionItem)[field] = value as never;
      return update;
    });
  }

  // TODO: retries/error handling
  const handleDefinitionBlur = useCallback(
    async (index: number, deleting = false) => {
      const definition = definitions?.[index];
      if (!definition) return;
      // New or edited definitions are always for the Case
      // Update
      if (definition.id && definition.caseId === id) {
        definition.isDeleted = deleting;
        await definitionsApi.definitionUpdate({ definition });
      } else {
        // Create
        definition.caseId = id;
        definition.id = undefined;
        definition.isInternal = true;
        definition.content = definition.content ?? "";
        definition.isDeleted = deleting;
        const created = await definitionsApi.definitionCreate({ definition });
        if (created.id) {
          setDefinitions((ds) => {
            const update = [...(ds ?? [])];
            update[index] = created;
            return update;
          });
        }
      }
    },
    [definitions, definitionsApi, id],
  );

  function handleDefinitionRemove(index: number) {
    setDefinitions((ds) => {
      const update = [...(ds ?? [])];
      update[index].toDelete = true;
      return update;
    });
  }

  const canSaveCase = useMemo(() => {
    if (_case?.clientPosition === PartyPosition.Unknown) {
      return false;
    }

    return true;
  }, [_case]);

  const handleProcessRequests = useCallback(
    async (openInWord: boolean) => {
      //setSaving(openInWord ? 'Word' : 'Save');
      if (caseIsDirty) {
        await caseApi.casePut({ id, firmId: user?.firmId ?? "", _case });
        setCaseIsDirty(false);
      }

      for (let i = 0; i < (definitions?.length ?? 0); i++) {
        const d = definitions?.[i];

        // any existing definitions to be deleted
        // OR New definitions that didn't previously get saved (empty)
        if ((d?.id && d?.caseId && d?.toDelete) || !d?.id) {
          await handleDefinitionBlur(i, d?.toDelete);
        }
      }

      const allQuestions = [...(questions ?? [])];
      const questionsToSave: { q: QuestionItem; toSave: MiscContent }[] = [];
      for (let i = 0; i < allQuestions.length; i++) {
        const g = allQuestions[i];

        if (g.key !== "-1") {
          for (let j = 0; j < (g.questions?.length ?? 0); j++) {
            const q = g.questions?.[j];
            if (q?.isChecked && q?.saveToLibrary) {
              questionsToSave.push({
                q,
                toSave: {
                  caseTypeId: g.caseType,
                  content: q.text,
                  type: MiscContentType.DiscoveryQuestion,
                  documentType: DocumentType.DiscoveryRequest,
                  documentSubType: docType,
                  isForRepresentative:
                    _case?.representativeType === undefined ||
                    _case.representativeType === Representative.None
                      ? undefined
                      : _case.representativeType !== Representative.Individual,
                  tagIds: g.tagIds,
                },
              });
            }
          }
        }
      }

      let results: unknown[] = [];
      const limit = 6;
      for (let start = 0; start < questionsToSave.length; start += limit) {
        const end =
          start + limit > questionsToSave.length
            ? questionsToSave.length
            : start + limit;

        const slicedResults = await Promise.all(
          questionsToSave.slice(start, end).map((x) =>
            createMiscContent(x.toSave).then((val) => {
              x.q.saveToLibrary = false;
              x.q.questionId = val.id;
            }),
          ),
        );

        results = [...results, ...slicedResults];
      }

      if (results.length) {
        setQuestions(allQuestions);
      }

      const createDiscoveryRequest: CreateDiscoveryRequest = {
        caseId: _case?.id ?? "",
        clientId: client?.id ?? "",
        setNumber: setNumber,
        startingNumber: startingNumber,
        documentSubType: docType,
        respondingId: party?.id,
        questions: allQuestions
          ?.flatMap((x) => x.questions)
          .filter((x) => x?.isChecked)
          .map<Question>((q, index) => {
            return {
              questionId: q?.questionId,
              text: q?.text,
              number: index + 1,
              interrogatoryId: q?.interrogatoryId,
            };
          }),
      };

      let docId = document?.id;
      if (!docId) {
        const created = await documentApi.documentCreateDiscoveryRequest({
          createDiscoveryRequest,
        });
        updateDocumentAndReloadQuestions(created);
        docId = created.id;
      } else {
        const updated = await documentApi.documentPutDiscoveryRequest({
          documentId: docId,
          createDiscoveryRequest,
        });

        updateDocumentAndReloadQuestions(updated);
      }

      if (openInWord) {
        const token = await documentApi.documentGenerateAccessToken({
          documentId: docId,
        });
        const url = getUrl("document/generate-doc-accesstoken", {
          documentId: docId,
          tokenId: token.id ?? "",
          t: Date.now().toString(),
        });

        window.open(url, "_blank");
      }
      setSaving(undefined);
      setIsDirty(false);
    },
    [
      _case,
      caseApi,
      caseIsDirty,
      client,
      createMiscContent,
      definitions,
      docType,
      document?.id,
      documentApi,
      handleDefinitionBlur,
      id,
      party?.id,
      questions,
      setNumber,
      startingNumber,
      user,
      updateDocumentAndReloadQuestions,
    ],
  );

  useEffect(() => {
    if (dirty) {
      updateQueue.current.addSave(() => handleProcessRequests(false), "Save");

      setIsDirty(false);
    }
  }, [dirty, handleProcessRequests]);

  const options: CaseTypeOption[] = useMemo(() => {
    if (!caseTypes || !jx?.caseClasses) {
      return [];
    }
    const vs = caseTypes?.flatMap((v) => [
      {
        value: v.id ?? "",
        label: v.shortName ?? "",
        key: jx?.caseClasses?.find((x) => x.id === v.caseClassId)?.name ?? "",
      },
    ]);
    return vs;
  }, [caseTypes, jx?.caseClasses]);

  function handleScrollToElement(ref: RefObject<HTMLDivElement>): void {
    setToastRequestOpen(true);
    // Add a small delay to make sure the form is visible first after clicking 'add now' so that the ref exists before scrolling to it.
    setTimeout(() => {
      ref.current?.scrollIntoView({
        behavior: "smooth",
      });
    }, 100);
    resetToastParams();
  }

  function handleCaseTypeChange(caseType: string) {
    setCaseIsDirty(true);
    setCase((c) => {
      if (!c) return c;
      return { ...c, caseType: caseType };
    });
  }

  function handleTagChange(
    value: Options<TagOption>,
    _: ActionMeta<TagOption>,
  ) {
    // If showToast is true before choosing a new tag, set it back to false again so we don;t wind up with the message blinking/flashing with the new selected tag...
    if (showToast) {
      resetToastParams();
    }

    const newTagIds = value?.map((x) => x.value ?? "") ?? [];

    setCaseIsDirty(true);

    if (_case?.tagIds) {
      prevTagIds.current = [..._case.tagIds];
    }

    setCase((c) => {
      if (!c) return c;
      return { ...c, tagIds: newTagIds as number[] };
    });

    if (_.action === "select-option") {
      const cleanedLabel = _.option?.label.replace(/\s*\[\d+\]/g, "").trim();
      const match = _.option?.label.match(/\[(\d+)\]$/);
      if (match) {
        setDoTagsExistInLibrary(true);
      } else {
        setDoTagsExistInLibrary(false);
      }

      setToastMsgValue(cleanedLabel);
      setToastCurTagId(_.option?.value);
      setToastPromptType("add");
    } else {
      resetToastParams();
      setCaseIsDirty(false);
    }
  }

  function handleChangeSetNumber(value: string): void {
    setSetNumber(Number(value));

    if (setNumber > 1 && value === "1") {
      setStartingNumber(1);
    }
  }

  function filterTags(tags: Map<number, string> | undefined, ids: number[]) {
    const filtered = new Map<number, string>();

    if (!tags) return filtered;

    ids.forEach((x) => {
      const t = tags.get(x);
      if (t) {
        filtered.set(x, t);
      }
    });

    return filtered;
  }

  function handleCaseHeaderClick(): void {
    setCaseDetailsOpen((o) => !o);
  }

  return (
    <>
      <LeavingGuard
        when={dirty || caseIsDirty}
        message="You have unsaved changes, continue and lose them?"
        title="Unsaved changes!"
      />
      <Container className="page-container generate-request-container">
        <Loading
          isLoading={
            user === undefined ||
            firm === undefined ||
            _case === undefined ||
            client === undefined ||
            jurisdictions === undefined ||
            questions === undefined ||
            definitions === undefined
          }
        >
          <div className="header-container">
            <h1 className="request-header">
              Draft {requestType} (Beta
              <span className="info-link">
                <Link
                  to="/tutorials#discovery_requests"
                  title="Learn More About Briefpoint's Propounding Discovery Beta"
                  target="_blank"
                >
                  <InfoCircle />
                </Link>
              </span>
              )
            </h1>
            <span className="header-span">
              <Link to={`/clients/${client?.id}`}>{client?.name}</Link>
            </span>
            &nbsp;/&nbsp;
            <span className="header-span">
              <Link to={`/clients/${client?.id}/cases/${_case?.id}`}>
                {_case?.shortTitle || _case?.title}
              </Link>
            </span>
          </div>
          <Accordion
            defaultActiveKey="case-details"
            className="accordion-main"
            onSelect={() => handleCaseHeaderClick()}
          >
            {/*** BEGIN CASE INFORMATION ***/}
            <Accordion.Item eventKey="case-details">
              <Accordion.Header className="section-header">
                <span>Case Details</span>
                <span className="arrow-span">
                  {caseDetailsOpen ? <ChevronUp /> : <ChevronDown />}
                </span>
              </Accordion.Header>
              <Accordion.Body className="accordion-body-container section-body">
                {_case && (
                  <CaseInfo
                    _case={_case}
                    jurisdiction={jx}
                    onChange={handleCaseChange}
                  />
                )}
              </Accordion.Body>
            </Accordion.Item>

            <h2 className="section-header">Request Details</h2>
            <div className="section-body">
              {/*** BEGIN REQUESTS ***/}
              <Row>
                <Col className="col-4">
                  <label htmlFor="case-type">Case Type</label>
                  <CaseTypeSelect
                    options={options}
                    onChange={handleCaseTypeChange}
                    selected={_case?.caseType ?? ""}
                  />
                </Col>
                <Col className="col-6">
                  <TagsSelect
                    id="case-tags"
                    label="Case Tags"
                    availableTags={caseTags}
                    extraInfo={extraTagInfo}
                    selected={_case?.tagIds ?? undefined}
                    onChange={handleTagChange}
                    useToast={true}
                    caseExist={toastExistingTag}
                    showToast={showToast}
                    scrollTo={() => {
                      if (doTagsExistInLibrary) {
                        handleOpenLibrary(
                          undefined,
                          toastCurTagId ?? undefined,
                        );
                      } else {
                        handleScrollToElement(
                          scrollToQuestion as RefObject<HTMLDivElement>,
                        );
                      }
                    }}
                    toastMsgValue={toastMsgValue}
                    toastPromptType={toastPromptType}
                    closeToast={resetToastParams}
                  />
                </Col>
                <Col className="col-1">
                  <label htmlFor="set-number">Set No.</label>
                  <Form.Control
                    id="set-number"
                    type="number"
                    min="1"
                    style={{ width: 80 }}
                    value={setNumber}
                    onChange={(e) => handleChangeSetNumber(e.target.value)}
                  />
                </Col>
                {setNumber > 1 && (
                  <Col className="col-1">
                    <label htmlFor="starting-number">Starting No.</label>
                    <Form.Control
                      id="starting-number"
                      type="number"
                      min="1"
                      style={{ width: 80 }}
                      value={startingNumber}
                      onChange={(event) =>
                        setStartingNumber(
                          Number.parseInt(event.currentTarget.value, 10),
                        )
                      }
                    />
                  </Col>
                )}
              </Row>
              <Row>
                <Col className="col-4">
                  <label htmlFor="party-select" className="padding form-label">
                    Responding Party
                  </label>
                  <p>{`${party?.position} ${party?.name}${party?.tagIds?.length ? `: ${party.tagIds.map((x) => availableTags?.get(x)).join(", ")}` : ""}`}</p>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowLibrary(true)}
                  >
                    Add from Library
                  </Button>
                </Col>
              </Row>
              {showLibrary && (
                <ModalRequestAddFromLib
                  selectedDocument={document}
                  addPosition={addPosition}
                  docSubType={docType}
                  confirmText={"Add"}
                  showLibrary={showLibrary}
                  modalSaving={modalSaving}
                  caseInfo={_case}
                  checkedItems={checkedItems}
                  questions={miscQuestions}
                  filteredSourceIdRequests={filteredSourceIdRequests}
                  tagSectionToOpen={tagSectionToOpen}
                  tagIdToFind={tagIdToFind}
                  handleRadioChange={handleRadioChange}
                  handleShowLibrary={handleShowLibrary}
                  showPositionControls={false}
                  addToGeneratedList={addToGeneratedList}
                  determineCheck={determineCheck}
                  handleCheckboxChange={handleCheckboxChange}
                  setCheckedItems={setCheckedItems}
                />
              )}
              {client && (
                <Questions
                  _case={_case}
                  user={user}
                  client={client}
                  otherParty={party}
                  startingNumber={startingNumber}
                  jurisdiction={jx}
                  questions={questions}
                  docType={docType}
                  checkForDefinitionUpdate={extractDefinitionsFromQuestions}
                  definitions={definitions}
                  availableTags={filterTags(
                    availableTags,
                    (_case?.tagIds ?? []).concat(party?.tagIds ?? []),
                  )}
                  setQuestions={setQuestions}
                  setIsDirty={setIsDirty}
                  toastRequestOpen={toastRequestOpen}
                  setToastRequestOpen={setToastRequestOpen}
                  scrollRef={scrollToQuestion}
                  handleDeleteItem={handleDeleteItem}
                  handleOpenLibrary={handleOpenLibrary}
                />
              )}
            </div>
            {/*** BEGIN DEFINITIONS ***/}
            <h2 className="section-header">Definitions</h2>
            <div className="section-body">
              <Definitions
                _case={_case}
                definitions={definitions}
                onChange={handleDefinitionChange}
                onBlur={handleDefinitionBlur}
                onRemove={handleDefinitionRemove}
              />
            </div>
          </Accordion>
          <Row className="actions-bar">
            <Col className="col-8 col-link">
              <Link to={`/clients/${client?.id}/cases/${_case?.id}`}>
                {"< Back to Case"}
              </Link>
            </Col>
            <Col
              className="action-buttons"
              style={{ justifyContent: "flex-end", display: "flex" }}
            >
              <Button
                variant="outline-primary"
                onClick={() =>
                  updateQueue.current.addSave(
                    () => handleProcessRequests(false),
                    "Save",
                  )
                }
                disabled={!canSaveCase || !!saving || (!dirty && !caseIsDirty)}
              >
                {saving === "Save" ? (
                  <>
                    <Spinner size="sm" /> Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>

              <Button
                variant="primary"
                onClick={() =>
                  updateQueue.current.addSave(
                    () => handleProcessRequests(true),
                    "Save",
                  )
                }
                disabled={!canSaveCase || !!saving}
              >
                {saving === "Word" ? (
                  <>
                    <Spinner size="sm" /> Generating...
                  </>
                ) : (
                  "Open in Word"
                )}
              </Button>
            </Col>
          </Row>
        </Loading>
      </Container>
    </>
  );
}
